import type { OnInit, OnDestroy } from '@angular/core';
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { RouterOutlet, Router, NavigationEnd } from '@angular/router';
import { HeaderComponent } from './core/layout/components/header/header.component';
import { FooterComponent } from './core/layout/components/footer/footer.component';
import { GlobalLoaderComponent } from './shared/components/global-loader/global-loader.component';
import { AuthService } from './core/services/auth.service';
import { SessionTimeoutService } from './core/services/session-timeout.service';
import { Subject, filter, takeUntil } from 'rxjs';

@Component({
  selector: 'app-root',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RouterOutlet, HeaderComponent, FooterComponent, GlobalLoaderComponent],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss'
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'befair';

  private authService = inject(AuthService);
  private sessionTimeoutService = inject(SessionTimeoutService);
  private router = inject(Router);
  private destroy$ = new Subject<void>();

  ngOnInit(): void {
    this.initializeSessionMonitoring();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.sessionTimeoutService.stopMonitoring();
  }

  /**
   * Initialize session timeout monitoring
   * Starts monitoring when user is authenticated and navigates to protected routes
   */
  private initializeSessionMonitoring(): void {
    // Watch for navigation to protected routes
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        const currentUser = this.authService.currentUserValue;

        // Check if user is authenticated
        if (currentUser?.payload?.exp) {
          // Start session monitoring - service will check actual token expiration time
          this.sessionTimeoutService.startMonitoring();
        }
      });
  }
}
